import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TaskAction from "../actions/TaskAction";
import BucketAction from "../actions/BucketAction";
import { useQuery } from "../AppLayout";
import { useCurrentSize } from "../CurrentSize";
import LabelAction from "../actions/LabelAction";
import { TemplateContexts } from "../constants/action-types";
import InboxAction from "../actions/InboxAction";
import { AppContext } from "../Contexts";
import { getJirItemUrl } from "../api/api";
import { navigateTo } from "../Navigator";
import { NoRecordEmptyState } from "../icons/EmptyStates";
import ItemScoringAction from "../actions/ItemScoringAction";
import TemplateAction from "../actions/TemplateAction";
import CommentsPanel from "./item-view/Left/CommentsPanel";
import { fromJS } from "immutable";
import { useState } from "react";
import ItemLinkAction from "../actions/ItemLinkAction";
import { getAppLink, getCurrentAzureOwner, getCurrentGoogleOwner } from "../api/AppLinkApi";
import DealDetailPanel from "../board/components/TaskDetail/detail-panel/DealDetailPanel";
import ContactDetailPanel from "../board/components/TaskDetail/detail-panel/ContactDetailPanel";
import AccountDetailPanel from "../board/components/TaskDetail/detail-panel/AccountDetailPanel";
import ProductDetailPanel from "../board/components/TaskDetail/detail-panel/ProductDetailPanel";
import ItemActivityLogs, { ActivityTabs } from "../activities/ItemActivityLogs";
import ImagesPanel from "./item-view/Left/ImagesPanel";
import ActivityAction from "../actions/ActivityAction";

const ItemView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, itemId }: any = useParams();
  const currentParams = useQuery();
  const contentType = currentParams.get("type") || "SALE";
  const backUrl = useQuery().get("backUrl");
  const tabParam = useQuery().get("tab") || "";

  const appContext = useContext<any>(AppContext);
  const currentSize = useCurrentSize();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));

  const item = useSelector((state: any) => state.taskReducer.getIn(["task", contentType]));
  const errorStatus =
    useSelector((state: any) => state.projectReducer.getIn(["error", "status"])) || "200";

  const allActivities =
    useSelector((state: any) => state.activityReducer.get("activities")) || fromJS([]);
  const itemActivities = allActivities
    .filter((log) => log.get("itemId") === itemId)
    .filter((log) =>
      log.getIn(["event", "data", "visibility"]) === "Private"
        ? log.getIn(["event", "person"]) === currentUser.get("accountId")
        : true
    );

  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [appGoogleData, setAppGoogleData] = useState<any>(fromJS({}));
  const [appAzureData, setAppAzureData] = useState<any>(fromJS({}));

  useEffect(() => {
    loadGoogleData();
    loadAzureData();
    dispatch(InboxAction.hideMessageView());
    dispatch(TemplateAction.getTemplates());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (item.get("id")) {
      document.title = item.getIn(["data", "name", "value"]) || "View Item";
    }
  }, [item]);

  useEffect(() => {
    if (contentType) {
      dispatch(
        TemplateAction.getAttributes([
          contentType,
          TemplateContexts.SENDER,
          TemplateContexts.RECIPIENT,
        ])
      );
    }
  }, [contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (itemId) {
      dispatch(TaskAction.getItem(itemId));
      dispatch(ItemLinkAction.getItemLink(itemId));
      dispatch(ItemScoringAction.getConnectionStrength(itemId));
      dispatch(ItemScoringAction.getEstimatedProbability(itemId));
    }
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (projectId && contentType) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
      if (contentType !== "SALE") {
        dispatch(BucketAction.getBuckets(projectId, "SALE"));
      }
      dispatch(LabelAction.getLabels(projectId, contentType));
    }
  }, [projectId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (itemId) {
      setIsLoadingActivities(true);
      dispatch(
        ActivityAction.searchActivities({
          itemId,
          scope: "item",
          includeDependencies:
            contentType === "COMPANY" || contentType === "CONTACT" || contentType === "SALE",
        })
      ).finally(() => {
        setIsLoadingActivities(false);
      });
    }
  }, [itemId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogleData = () => {
    getAppLink("google")
      .then((data) => {
        if (data.connected) {
          getCurrentGoogleOwner()
            .then((dataUser) => {
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: dataUser.name,
                  email: dataUser.email,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.info("Failed to get Google owner:", error);
              // Set state to disconnected when API fails
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: t("Anonymous"),
                  email: "",
                  connected: false,
                  ...data,
                })
              );
            });
        } else {
          setAppGoogleData(
            fromJS({
              app: "google",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.info("Failed to get Google app link:", error);
        // Set default state when app link fetch fails
        setAppGoogleData(
          fromJS({
            app: "google",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const loadAzureData = () => {
    getAppLink("azure")
      .then((data) => {
        if (data.connected) {
          getCurrentAzureOwner()
            .then((dataUser) => {
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: dataUser.displayName,
                  email: dataUser.mail,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.info("Failed to get Azure owner:", error);
              // Set state to disconnected when API fails
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: t("Anonymous"),
                  email: "",
                  connected: false,
                  ...data,
                })
              );
            });
        } else {
          setAppAzureData(
            fromJS({
              app: "azure",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.info("Failed to get Azure app link:", error);
        // Set default state when app link fetch fails
        setAppAzureData(
          fromJS({
            app: "azure",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const getBackUrl = () => {
    if (backUrl) {
      return backUrl;
    }
    if (contentType == "SALE") {
      return `/workspaces/${projectId}?view=BOARD&type=SALE&key=Deals`;
    }
    if (contentType == "COMPANY") {
      return `/workspaces/${projectId}?view=GRID&type=COMPANY&key=Accounts`;
    }
    if (contentType == "CONTACT") {
      return `/workspaces/${projectId}?view=GRID&type=CONTACT&key=Contacts`;
    }
    if (contentType == "PRODUCT") {
      return `/workspaces/${projectId}?view=GRID&type=PRODUCT&key=Products`;
    }
  };

  const handleCloseView = () => {
    navigate(getBackUrl());
  };

  const handleFullView = () => {
    const appUrl = `/workspaces/${projectId}/items/${itemId}?type=${contentType}`;
    const jiraUrl = getJirItemUrl({
      workspaceId: projectId,
      itemId,
      type: contentType,
      view: "BOARD",
    });
    navigateTo({ jiraUrlParams: { relativeUrl: jiraUrl }, appUrl, navigate });
  };

  const saleNavRef = useRef<any>(null);
  const summaryHeaderRef = useRef<any>(null);

  const saleNavHeight = () => {
    if (saleNavRef.current) {
      return saleNavRef.current.clientHeight;
    }
    return 0;
  };

  const getSummaryHeaderHeight = () => {
    if (summaryHeaderRef.current) {
      return summaryHeaderRef.current.clientHeight + saleNavHeight() + 80;
    }
    return saleNavHeight() + 80;
  };

  const renderImagePanel = () => {
    if (!item) return null;
    if (contentType === "PRODUCT") {
      const imageIds = item.getIn(["data", "imageIds", "value"]) || fromJS([]);
      return (
        <ImagesPanel
          itemId={item.get("id")}
          value={imageIds.toJS()}
          onChange={(imageIds) => {
            dispatch(TaskAction.updateTask(itemId, "imageIds", imageIds.join(",")));
          }}
        />
      );
    }
    return null;
  };

  const renderDetailPanel = () => {
    switch (contentType) {
      case "SALE":
        return <DealDetailPanel projectId={projectId} contentType={contentType} />;
      case "CONTACT":
        return <ContactDetailPanel projectId={projectId} contentType={contentType} />;
      case "COMPANY":
        return <AccountDetailPanel projectId={projectId} contentType={contentType} />;
      case "PRODUCT":
        return <ProductDetailPanel projectId={projectId} contentType={contentType} />;
      default:
        return null;
    }
  };

  const renderItemSummary = () => {
    const isCompact = true;
    return (
      <div
        className={`col-span-1 space-y-2 p-4 pr-0 bg-white rounded-lg ${
          isCompact ? "" : "shadow"
        } lg:h-full lg:flex lg:flex-col`}
      >
        <div
          className={"w-full lg:overflow-y-auto lg:flex-1 lg:min-h-0 space-y-3"}
          style={{
            maxHeight:
              window.innerWidth >= 1024 ? currentSize.height - getSummaryHeaderHeight() : "none",
          }}
        >
          {renderImagePanel()}
          {renderDetailPanel()}
          <CommentsPanel
            itemId={itemId}
            itemType={contentType}
            itemLabel={item.getIn(["data", "name", "value"]) || ""}
            itemActivities={itemActivities}
            isLoading={isLoadingActivities}
          />
        </div>
      </div>
    );
  };

  const renderBackAction = () => {
    if (appContext.appName == "issueApp") {
      return (
        <div className={"lg:hidden sm:w-full space-y-2"}>
          <button
            type="button"
            onClick={handleCloseView}
            className="w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {t("Close")}
          </button>
          <button
            type="button"
            onClick={handleFullView}
            className="w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {t("View")}
          </button>
        </div>
      );
    }
  };

  const errorMessage = () => {
    switch (errorStatus) {
      case 404:
        return t("Sorry, we couldn’t find the records you’re looking for.");
      case 401:
        return t("Permission denied.");
    }
  };

  if (errorStatus == 401 || errorStatus == 404) {
    return (
      <div className={"mx-auto max-w-4xl text-center items-center space-y-4"}>
        <div className={"mt-2 text-sm font-semibold text-gray-900"}>{errorMessage()}</div>
        <div className={"w-fit"}>
          <NoRecordEmptyState />
        </div>
      </div>
    );
  }

  const renderOverviewTabContent = () => {
    return (
      <div className="lg:h-full lg:flex lg:flex-col lg:overflow-hidden bg-white">
        <div className="lg:flex-1 pt-4 pl-3 pr-3 lg:min-h-0 flex flex-col gap-6 lg:overflow-y-auto bg-white">
          <div className="flex-1 min-h-0">
            <ItemActivityLogs
              projectId={projectId}
              item={item}
              itemId={itemId}
              itemType={contentType}
              itemActivities={itemActivities}
              allActivities={allActivities}
              activityAction={
                tabParam ||
                (contentType === "PRODUCT"
                  ? ActivityTabs.DEAL_PRODUCT.key
                  : ActivityTabs.OVERVIEW.key)
              }
              appGoogleData={appGoogleData}
              appAzureData={appAzureData}
              isLoading={isLoadingActivities}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className={"lg:overflow-y-hidden lg:h-full w-full overflow-y-auto"}>
      {renderBackAction()}
      <div className="lg:h-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-2 lg:h-full lg:min-h-0">
          {renderItemSummary()}
          <div
            className={
              "w-full col-span-2 lg:h-full lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden lg:border-l lg:border-gray-200 lg:border-r lg:border-white-200"
            }
          >
            {renderOverviewTabContent()}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ItemView;
